import { useRouterState } from '@tanstack/react-router';
import { MessageSquareText, MousePointerClick, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  canEmbed,
  chatEmbedUrl,
  type ChatContext,
  partOf,
  SMART_BUILDER,
  type SmartBuilderLink,
} from '../ai-chat.ts';
import { can, useMe } from '../auth.tsx';
import { Button } from './ui.tsx';

/** O chat do Smart Builder num quadro, com a tela e a parte escolhida (ADR 0125). */
export function AiChatFrame({
  link,
  context,
  className,
}: {
  link: SmartBuilderLink;
  context: ChatContext;
  className?: string;
}) {
  const { t } = useTranslation();
  const frame = useRef<HTMLIFrameElement>(null);
  // O endereço fica o da abertura; a parte escolhida depois vai por mensagem (sem recarregar).
  const [src] = useState(() => chatEmbedUrl(link, context));
  useEffect(() => {
    frame.current?.contentWindow?.postMessage(
      { type: 'beadev-chat-context', page: context.page, part: context.part },
      new URL(link.url).origin,
    );
  }, [link, context]);
  return (
    <iframe
      ref={frame}
      title={t('aiChat.title')}
      src={src}
      className={className}
      onLoad={() =>
        frame.current?.contentWindow?.postMessage(
          { type: 'beadev-chat-context', page: context.page, part: context.part },
          new URL(link.url).origin,
        )
      }
    />
  );
}

/**
 * O botão flutuante: em qualquer tela, quem pode pedir mudanças abre o chat da IA ao lado, já
 * sabendo em que tela está, e pode escolher uma parte da tela para mudar.
 */
export function AiChatButton() {
  const { t } = useTranslation();
  const me = useMe();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const [part, setPart] = useState<string | undefined>();

  useEffect(() => {
    if (!picking) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target || target.closest('[data-ai-chat]')) return;
      e.preventDefault();
      e.stopPropagation();
      setPart(partOf(target));
      setPicking(false);
      setOpen(true);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setPicking(false);
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey);
    document.body.style.cursor = 'crosshair';
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey);
      document.body.style.cursor = '';
    };
  }, [picking]);

  const link = SMART_BUILDER;
  if (!link || !can(me.data, 'platform.ai.chat')) return null;
  const context: ChatContext = { page: `${path} (${document.title})`, part };
  const embed = canEmbed(window.location.hostname, link);

  return (
    <div data-ai-chat>
      {picking && (
        <p
          role="status"
          className="fixed top-3 left-1/2 z-50 -translate-x-1/2 rounded-md bg-ink px-3 py-2 text-sm text-sheet shadow-lg"
        >
          {t('aiChat.picking')}
        </p>
      )}
      {open && embed && (
        <aside
          aria-label={t('aiChat.title')}
          className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-rule bg-sheet shadow-xl"
        >
          <div className="flex items-center gap-2 border-b border-rule p-2">
            <h2 className="flex-1 text-sm font-semibold">{t('aiChat.title')}</h2>
            <Button variant="secondary" onClick={() => setPicking(true)}>
              <MousePointerClick size={16} aria-hidden className="mr-1 inline" />
              {t('aiChat.pick')}
            </Button>
            <button
              type="button"
              aria-label={t('aiChat.close')}
              className="rounded-md p-1 text-muted hover:text-ink"
              onClick={() => setOpen(false)}
            >
              <X size={18} aria-hidden />
            </button>
          </div>
          {part && <p className="px-3 pt-2 text-xs text-muted">{t('aiChat.picked', { part })}</p>}
          <AiChatFrame link={link} context={context} className="min-h-0 w-full flex-1" />
        </aside>
      )}
      {!open && (
        <button
          type="button"
          className="fixed right-4 bottom-4 z-40 flex items-center gap-2 rounded-full bg-accent px-4 py-2 text-sm font-medium text-on-accent shadow-lg hover:opacity-90"
          onClick={() =>
            embed ? setOpen(true) : window.open(chatEmbedUrl(link, context), '_blank', 'noopener')
          }
        >
          <MessageSquareText size={18} aria-hidden />
          {t('aiChat.open')}
        </button>
      )}
    </div>
  );
}
